import React, { useMemo, useState } from "react";
import { RootStateOrAny, useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "office-ui-fabric-react/lib/Button";
import { IDropdownOption } from "office-ui-fabric-react/lib/Dropdown";
import { Dialog } from "office-ui-fabric-react/lib/Dialog";
import { mergeStyles } from "office-ui-fabric-react";

import { errorButtonS } from "../../common/styles/errorBtn.styles";
import DialogErr from "../../common/layout/DialogErr";
import { useEffectAfterMount } from "../../common/hooks/useEffectAfterMount";
import { setListMessage } from "../../../config/redux/alerts/alertsActions";
import { deactivateCompany } from "../../../connectors/clients/connectors";
import {
  reloadData,
  toggleDeactivationReason,
} from "../../../config/redux/clients/clientsActions";
import { useQuery } from "react-query";
import { getReasonsById, getReasonsSourceValuesById } from "../../../connectors/cancellationReasons/connectors";
import DeactivationReasonDropDown from "./DeactivationReasonDropDown";
import { Reason } from "../../common/enums/reason.enum";

const messageContainer = mergeStyles({
  width: "100%",
  height: 15,
  marginTop: 10,
  marginBottom: 20,
});

export interface DeactivationReasonProps {};

const DeactivationReason: React.FC<DeactivationReasonProps> = () => {
  const { t } = useTranslation("admin");
  const [errVisible, changeErrVisibility] = useState(false);
  const [selectedReason, setSelectedReason] = React.useState<IDropdownOption>();
  const { data } = useQuery(
    ["deactivationReasons", Reason.ClientDeactivation],
    getReasonsById
  );
  // Untranslated values - the reason is stored in the database as is, so it must stay in the source language
  const { data: sourceData } = useQuery(
    ["deactivationReasonsSource", Reason.ClientDeactivation],
    getReasonsSourceValuesById
  );
  const sourceValues = useMemo(() => {
    const map: { [id: string]: string } = {};
    if (sourceData) {
      sourceData.data.data.forEach((reason: any) => { map[reason.id] = reason.value; });
    }
    return map;
  }, [sourceData]);
  const reasons = useMemo(() => {
    if (data) {
      return data.data.data.map((reason: any) => ({
        key: reason.id,
        text: reason.value,
      }));
    } else return [];
  }, [data]);

  const dispatch = useDispatch();
  const { deactivationReasonOpen, selectedClients } = useSelector(
    (state: RootStateOrAny) => state.clients
  );

  const dismiss = () => {
    dispatch(toggleDeactivationReason(false));
    changeErrVisibility(false);
    setSelectedReason(undefined);
  };

  const setOrderCanceled = async () => {
    if (selectedReason) {
      try {
        const reasonValue = sourceValues[selectedReason.key] || selectedReason.text.toString();
        await deactivateCompany(selectedClients, reasonValue);
        dispatch(reloadData({ reload: true }));
        dispatch(toggleDeactivationReason(false))
        setSelectedReason(undefined)
        dispatch(setListMessage(t("nav.operationSuccess")));
      } catch (err) {}
    } else {
      changeErrVisibility(true);
    }
  };

  useEffectAfterMount(() => {
    return () => {
      setSelectedReason(undefined);
    };
  }, []);

  return (
    <Dialog hidden={!deactivationReasonOpen} onDismiss={dismiss}>
      <DeactivationReasonDropDown
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
        options={reasons}
      />
      <PrimaryButton
        styles={errorButtonS}
        text={t("nav.deactivate") as string}
        onClick={setOrderCanceled}
      />
      <div className={messageContainer}>
        {errVisible && <DialogErr message={t("nav.selectDeactivationReason") as string} />}
      </div>
    </Dialog>
  );
};

export default DeactivationReason;
